import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import OrganizationStripeModes from '../components/OrganizationStripeModes';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  EnvelopeIcon,
  UsersIcon,
  CogIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserGroupIcon as AgeGroupIcon,
} from '@heroicons/react/24/outline';

const AppManagement = () => {
  const { user } = useAuth();
  const [confirmState, setConfirmState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'player',
    teamId: '',
    organizationId: '',
    organizationRole: 'viewer',
    is_active: true
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTeams: 0,
    totalOrganizations: 0,
    activeUsers: 0,
    recentActivity: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = useState(false);
  const [selectedOrgForAdmin, setSelectedOrgForAdmin] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [newOrgData, setNewOrgData] = useState({
    name: '',
    description: ''
  });
  const [assignAdminData, setAssignAdminData] = useState({
    userId: '',
    role: 'admin'
  });
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [showEditOrgModal, setShowEditOrgModal] = useState(false);
  const [showCreateAgeGroupModal, setShowCreateAgeGroupModal] = useState(false);
  const [showEditAgeGroupModal, setShowEditAgeGroupModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editPlanData, setEditPlanData] = useState({ name: '', description: '', amount: '', stripe_price_id: '', stripe_product_id: '', interval: 'month', is_active: true, display_order: 0, features: [], max_teams: '', max_users_per_team: '' });
  const [newPlanData, setNewPlanData] = useState({ name: '', description: '', amount: '', stripe_price_id: '', stripe_product_id: '', interval: 'month', interval_count: 1, is_active: true, display_order: 0, features: [], max_teams: '', max_users_per_team: '' });
  const [editUserData, setEditUserData] = useState({ name: '', email: '', role: '', is_active: true });
  const [editTeamData, setEditTeamData] = useState({ name: '', sport: '', ageGroup: '', age_group_id: '', season: '', organization_id: '', isActive: true });
  const [editOrgData, setEditOrgData] = useState({ name: '', description: '' });
  const [newAgeGroupData, setNewAgeGroupData] = useState({ name: '', description: '', min_age: '', max_age: '', min_birthday: '', max_birthday: '', cutoff_date: '', organization_id: '', is_active: true, sort_order: 0 });
  const [editAgeGroupData, setEditAgeGroupData] = useState({ name: '', description: '', min_age: '', max_age: '', min_birthday: '', max_birthday: '', cutoff_date: '', organization_id: '', is_active: true, sort_order: 0 });
  const [platformStripeAccount, setPlatformStripeAccount] = useState(null);
  const [platformStripeLoading, setPlatformStripeLoading] = useState(false);

  useEffect(() => {
    fetchAppData();
  }, []);

  useEffect(() => {
    if (activeTab === 'organizations') {
      fetchOrganizations();
      fetchAllUsers();
    } else if (activeTab === 'users') {
      fetchUsers();
      // Fetch teams and orgs when users tab is active for creating users
      fetchTeams();
      fetchOrganizations();
    } else if (activeTab === 'teams') {
      fetchTeams();
      fetchOrganizations(); // Need orgs for assigning teams
      fetchAgeGroups(); // Needed for assigning teams to age groups
    } else if (activeTab === 'age-groups') {
      fetchAgeGroups();
      fetchOrganizations(); // Need orgs for assigning age groups
    } else if (activeTab === 'subscriptions') {
      fetchSubscriptionPlans();
    } else if (activeTab === 'platform-stripe') {
      fetchPlatformStripeAccount();
    }
  }, [activeTab]);

  // Also fetch teams and orgs when invite modal opens
  useEffect(() => {
    if (showInviteModal) {
      if (teams.length === 0) fetchTeams();
      if (organizations.length === 0) fetchOrganizations();
    }
  }, [showInviteModal]);

  const filteredAgeGroupsForTeam = useMemo(() => {
    const orgId = editTeamData.organization_id ? parseInt(editTeamData.organization_id, 10) : null;
    return ageGroups.filter(ag => {
      if (ag.is_active === false) return false;
      if (orgId === null || Number.isNaN(orgId)) {
        return ag.organization_id === null || ag.organization_id === undefined;
      }
      return ag.organization_id === null || ag.organization_id === undefined || ag.organization_id === orgId;
    });
  }, [ageGroups, editTeamData.organization_id]);

  const fetchAppData = async () => {
    try {
      setLoading(true);
      
      // Fetch app statistics
      const statsResponse = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Fetch recent activity
      const activityResponse = await fetch('/api/admin/activity', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (activityResponse.ok) {
        const activityData = await activityResponse.json();
        setRecentActivity(activityData);
      }
    } catch (error) {
      console.error('Error fetching app data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - only include non-empty optional fields
      const userData = {
        name: inviteData.name,
        email: inviteData.email,
        role: inviteData.role,
        is_active: inviteData.is_active
      };
      
      // Add password if provided
      if (inviteData.password && inviteData.password.trim()) {
        userData.password = inviteData.password;
      }
      
      // Add teamId if provided
      if (inviteData.teamId && inviteData.teamId !== '') {
        userData.teamId = parseInt(inviteData.teamId, 10);
      }
      
      // Add organizationId if provided
      if (inviteData.organizationId && inviteData.organizationId !== '') {
        userData.organizationId = parseInt(inviteData.organizationId, 10);
        userData.organizationRole = inviteData.organizationRole || 'viewer';
      }

      const response = await fetch('/api/admin/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        const result = await response.json();
        let message = 'User created successfully!';
        if (result.tempPassword) {
          message += ` Temporary password: ${result.tempPassword}`;
        }
        toast.success(message);
        setInviteData({
          email: '',
          name: '',
          password: '',
          role: 'player',
          teamId: '',
          organizationId: '',
          organizationRole: 'viewer',
          is_active: true
        });
        setShowInviteModal(false);
        fetchUsers(); // Refresh user list
      } else {
        toast.error('Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    }
  };

  const handleSystemAction = (action) => {
    setConfirmState({
      title: `Confirm ${action}`,
      message: `Are you sure you want to ${action}?`,
      isDangerous: true,
      onConfirm: async () => {
        try {
          const response = await fetch('/api/admin/system', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ action }),
          });
          if (response.ok) {
            toast.success(`System ${action} completed successfully!`);
          } else {
            toast.error(`Failed to ${action} system`);
          }
        } catch (error) {
          console.error(`Error ${action} system:`, error);
          toast.error(`Failed to ${action} system`);
        }
      },
    });
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch('/api/admin/organizations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched users for admin assignment:', data.length, 'users');
        setAllUsers(data);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to fetch users:', response.status, errorData);
        toast.error(`Failed to load users: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users. Please try again.');
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/admin/organizations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newOrgData)
      });

      if (response.ok) {
        toast.success('Organization created successfully!');
        setNewOrgData({ name: '', description: '' });
        setShowCreateOrgModal(false);
        fetchOrganizations();
        fetchAppData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error('Failed to create organization');
    }
  };

  const handleAssignAdmin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/admin/organizations/${selectedOrgForAdmin}/assign-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(assignAdminData)
      });

      if (response.ok) {
        toast.success('Admin assigned successfully!');
        setAssignAdminData({ userId: '', role: 'admin' });
        setShowAssignAdminModal(false);
        setSelectedOrgForAdmin(null);
        fetchOrganizations();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to assign admin');
      }
    } catch (error) {
      console.error('Error assigning admin:', error);
      toast.error('Failed to assign admin');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/admin/teams', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      role: user.role,
      is_active: user.is_active
    });
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editUserData)
      });

      if (response.ok) {
        toast.success('User updated successfully!');
        setShowEditUserModal(false);
        setSelectedUser(null);
        fetchUsers();
        fetchAppData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setEditTeamData({
      name: team.name,
      sport: team.sport,
      ageGroup: team.ageGroup,
      age_group_id: team.ageGroupId ? String(team.ageGroupId) : '',
      season: team.season,
      organization_id: team.organization?.id || '',
      isActive: team.isActive
    });
    setShowEditTeamModal(true);
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      const selectedAgeGroupId = editTeamData.age_group_id === '' ? null : parseInt(editTeamData.age_group_id, 10);
      const selectedAgeGroup = selectedAgeGroupId
        ? ageGroups.find(ag => ag.id === selectedAgeGroupId)
        : null;

      const payload = {
        ...editTeamData,
        organization_id: editTeamData.organization_id === '' ? null : parseInt(editTeamData.organization_id, 10),
        age_group_id: selectedAgeGroupId,
        ageGroup: selectedAgeGroup ? selectedAgeGroup.name : editTeamData.ageGroup
      };
      const response = await fetch(`/api/admin/teams/${selectedTeam.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Team updated successfully!');
        setShowEditTeamModal(false);
        setSelectedTeam(null);
        fetchTeams();
        fetchAppData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update team');
      }
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error('Failed to update team');
    }
  };

  const handleDeactivateTeam = (teamId) => {
    setConfirmState({
      title: 'Deactivate Team',
      message: 'Are you sure you want to deactivate this team?',
      isDangerous: true,
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/admin/teams/${teamId}/deactivate`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (response.ok) {
            toast.success('Team deactivated successfully!');
            fetchTeams();
            fetchAppData();
          } else {
            toast.error('Failed to deactivate team');
          }
        } catch (error) {
          console.error('Error deactivating team:', error);
          toast.error('Failed to deactivate team');
        }
      },
    });
  };

  const handleEditOrg = (org) => {
    setSelectedOrg(org);
    setEditOrgData({
      name: org.name,
      description: org.description || ''
    });
    setShowEditOrgModal(true);
  };

  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/admin/organizations/${selectedOrg.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editOrgData)
      });

      if (response.ok) {
        toast.success('Organization updated successfully!');
        setShowEditOrgModal(false);
        setSelectedOrg(null);
        fetchOrganizations();
        fetchAppData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update organization');
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error('Failed to update organization');
    }
  };

  const handleDeleteOrg = (orgId) => {
    setConfirmState({
      title: 'Delete Organization',
      message: 'Are you sure you want to delete this organization? This action cannot be undone.',
      isDangerous: true,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/admin/organizations/${orgId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (response.ok) {
            toast.success('Organization deleted successfully!');
            fetchOrganizations();
            fetchAppData();
          } else {
            toast.error('Failed to delete organization');
          }
        } catch (error) {
          console.error('Error deleting organization:', error);
          toast.error('Failed to delete organization');
        }
      },
    });
  };

  const fetchPlatformStripeAccount = async () => {
    setPlatformStripeLoading(true);
    try {
      const response = await fetch('/api/subscriptions/platform-account', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlatformStripeAccount(data);
      } else {
        const error = await response.json();
        setPlatformStripeAccount({ connected: false, error: error.error || error.message });
      }
    } catch (error) {
      console.error('Error fetching platform Stripe account:', error);
      setPlatformStripeAccount({ connected: false, error: 'Failed to fetch account information' });
    } finally {
      setPlatformStripeLoading(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await fetch('/api/subscriptions/plans?includeInactive=true', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionPlans(data);
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setEditPlanData({
      name: plan.name || '',
      description: plan.description || '',
      amount: plan.amount || '',
      stripe_price_id: plan.stripe_price_id || '',
      stripe_product_id: plan.stripe_product_id || '',
      interval: plan.interval || 'month',
      is_active: plan.is_active !== undefined ? plan.is_active : true,
      display_order: plan.display_order || 0,
      features: plan.features || [],
      max_teams: plan.max_teams || '',
      max_users_per_team: plan.max_users_per_team || ''
    });
    setShowEditPlanModal(true);
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/subscriptions/plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...newPlanData,
          amount: parseFloat(newPlanData.amount),
          interval_count: parseInt(newPlanData.interval_count) || 1,
          display_order: parseInt(newPlanData.display_order) || 0,
          max_teams: newPlanData.max_teams === '' ? null : parseInt(newPlanData.max_teams),
          max_users_per_team: newPlanData.max_users_per_team === '' ? null : parseInt(newPlanData.max_users_per_team),
          features: []
        })
      });

      if (response.ok) {
        toast.success('Subscription plan created successfully!');
        setShowCreatePlanModal(false);
        setNewPlanData({ name: '', description: '', amount: '', stripe_price_id: '', stripe_product_id: '', interval: 'month', interval_count: 1, is_active: true, display_order: 0, features: [], max_teams: '', max_users_per_team: '' });
        fetchSubscriptionPlans();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create subscription plan');
      }
    } catch (error) {
      console.error('Error creating subscription plan:', error);
      toast.error('Failed to create subscription plan');
    }
  };

  const handleUpdatePlan = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/subscriptions/plans/${selectedPlan.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editPlanData)
      });

      if (response.ok) {
        toast.success('Subscription plan updated successfully!');
        setShowEditPlanModal(false);
        setSelectedPlan(null);
        fetchSubscriptionPlans();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update subscription plan');
      }
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      toast.error('Failed to update subscription plan');
    }
  };

  const fetchAgeGroups = async () => {
    try {
      const response = await fetch('/api/age-groups/all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAgeGroups(data);
      }
    } catch (error) {
      console.error('Error fetching age groups:', error);
    }
  };

  const handleCreateAgeGroup = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newAgeGroupData,
        min_age: newAgeGroupData.min_age === '' ? null : parseInt(newAgeGroupData.min_age, 10),
        max_age: newAgeGroupData.max_age === '' ? null : parseInt(newAgeGroupData.max_age, 10),
        min_birthday: newAgeGroupData.min_birthday || null,
        max_birthday: newAgeGroupData.max_birthday || null,
        cutoff_date: newAgeGroupData.cutoff_date || null,
        organization_id: newAgeGroupData.organization_id === '' ? null : parseInt(newAgeGroupData.organization_id, 10),
        sort_order: parseInt(newAgeGroupData.sort_order, 10) || 0
      };
      const response = await fetch('/api/age-groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Age group created successfully!');
        setShowCreateAgeGroupModal(false);
        setNewAgeGroupData({ name: '', description: '', min_age: '', max_age: '', min_birthday: '', max_birthday: '', cutoff_date: '', organization_id: '', is_active: true, sort_order: 0 });
        fetchAgeGroups();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to create age group');
      }
    } catch (error) {
      console.error('Error creating age group:', error);
      toast.error('Failed to create age group');
    }
  };

  const handleEditAgeGroup = (ageGroup) => {
    // Ensure create modal is closed when opening edit modal
    setShowCreateAgeGroupModal(false);
    setSelectedAgeGroup(ageGroup);
    setEditAgeGroupData({
      name: ageGroup.name,
      description: ageGroup.description || '',
      min_age: ageGroup.min_age ?? '',
      max_age: ageGroup.max_age ?? '',
      min_birthday: ageGroup.min_birthday || '',
      max_birthday: ageGroup.max_birthday || '',
      cutoff_date: ageGroup.cutoff_date || '',
      organization_id: ageGroup.organization_id || '',
      // Handle both undefined/null and false values - default to true if not explicitly false
      is_active: ageGroup.is_active !== undefined && ageGroup.is_active !== null ? ageGroup.is_active : true,
      sort_order: ageGroup.sort_order || 0
    });
    setShowEditAgeGroupModal(true);
  };

  const handleUpdateAgeGroup = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editAgeGroupData,
        min_age: editAgeGroupData.min_age === '' ? null : parseInt(editAgeGroupData.min_age, 10),
        max_age: editAgeGroupData.max_age === '' ? null : parseInt(editAgeGroupData.max_age, 10),
        min_birthday: editAgeGroupData.min_birthday || null,
        max_birthday: editAgeGroupData.max_birthday || null,
        cutoff_date: editAgeGroupData.cutoff_date || null,
        organization_id: editAgeGroupData.organization_id === '' ? null : parseInt(editAgeGroupData.organization_id, 10),
        sort_order: parseInt(editAgeGroupData.sort_order, 10) || 0
      };
      const response = await fetch(`/api/age-groups/${selectedAgeGroup.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Age group updated successfully!');
        setShowEditAgeGroupModal(false);
        setSelectedAgeGroup(null);
        fetchAgeGroups();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update age group');
      }
    } catch (error) {
      console.error('Error updating age group:', error);
      toast.error('Failed to update age group');
    }
  };

  const handleDeleteAgeGroup = (ageGroupId) => {
    setConfirmState({
      title: 'Delete Age Group',
      message: 'Are you sure you want to delete this age group? This action cannot be undone.',
      isDangerous: true,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/age-groups/${ageGroupId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (response.ok) {
            toast.success('Age group deleted successfully!');
            fetchAgeGroups();
          } else {
            toast.error('Failed to delete age group');
          }
        } catch (error) {
          console.error('Error deleting age group:', error);
          toast.error('Failed to delete age group');
        }
      },
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <CogIcon className="h-8 w-8 text-blue-600 mr-3" />
              App Management
            </h1>
            <p className="text-gray-600 mt-1">
              System administration and management
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create User
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview', icon: ChartBarIcon },
              { id: 'users', name: 'Users', icon: UsersIcon },
              { id: 'teams', name: 'Teams', icon: UserGroupIcon },
              { id: 'organizations', name: 'Organizations', icon: BuildingOfficeIcon },
              { id: 'age-groups', name: 'Age Groups', icon: AgeGroupIcon },
              { id: 'subscriptions', name: 'Subscriptions', icon: CurrencyDollarIcon },
              { id: 'platform-stripe', name: 'Platform Stripe', icon: CurrencyDollarIcon },
              { id: 'system', name: 'System', icon: CogIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <UsersIcon className="h-8 w-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-600">Total Users</p>
                      <p className="text-2xl font-bold text-blue-900">{stats.totalUsers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <UserGroupIcon className="h-8 w-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">Total Teams</p>
                      <p className="text-2xl font-bold text-green-900">{stats.totalTeams}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-8 w-8 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-purple-600">Organizations</p>
                      <p className="text-2xl font-bold text-purple-900">{stats.totalOrganizations}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                      <span className="text-yellow-600 font-bold">A</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-yellow-600">Active Users</p>
                      <p className="text-2xl font-bold text-yellow-900">{stats.activeUsers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <CalendarIcon className="h-8 w-8 text-indigo-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-indigo-600">Recent Activity</p>
                      <p className="text-2xl font-bold text-indigo-900">{stats.recentActivity}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {activity.user?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                            <p className="text-sm text-gray-500">{activity.user}</p>
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(activity.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">User Management</h3>
              </div>

              <div className="space-y-4">
                {users.map((user) => (
                  <div key={user.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="text-lg font-semibold text-gray-900">{user.name}</h4>
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                            {user.role}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{user.email}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Created: {formatDate(user.createdAt)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Organizations */}
                    {user.organizations && user.organizations.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-2">Organizations</h5>
                        <div className="space-y-1">
                          {user.organizations.map((org) => (
                            <div key={org.id} className="flex items-center justify-between bg-purple-50 p-2 rounded">
                              <div>
                                <span className="font-medium">{org.name}</span>
                                <span className="text-xs text-gray-500 ml-2">({org.code})</span>
                              </div>
                              <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded">
                                {org.role}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Teams */}
                    {user.teams && user.teams.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-2">Teams</h5>
                        <div className="space-y-1">
                          {user.teams.map((team) => (
                            <div key={team.id} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                              <div>
                                <span className="font-medium">{team.name}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  {team.sport} • {team.ageGroup} • {team.season}
                                </span>
                                {team.organization && (
                                  <span className="text-xs text-gray-500 ml-2">
                                    • {team.organization.name}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                                {team.relationshipType}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!user.organizations || user.organizations.length === 0) && 
                     (!user.teams || user.teams.length === 0) && (
                      <p className="text-sm text-gray-500">No organizations or teams</p>
                    )}
                  </div>
                ))}
                {users.length === 0 && (
                  <p className="text-gray-600 text-center py-8">No users found.</p>
                )}
              </div>
            </div>
          )}

          {/* Teams Tab */}
          {activeTab === 'teams' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Team Management</h3>
              </div>

              <div className="space-y-4">
                {teams.map((team) => (
                  <div key={team.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="text-lg font-semibold text-gray-900">{team.name}</h4>
                          <span className={`px-2 py-1 rounded text-xs ${
                            team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {team.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {team.sport} • {team.ageGroup} • {team.season}
                        </p>
                        {team.organization && (
                          <p className="text-xs text-gray-500 mt-1">
                            Organization: {team.organization.name} ({team.organization.code})
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Code: {team.teamCode} • Created: {formatDate(team.createdAt)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditTeam(team)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        {team.isActive && (
                          <button
                            onClick={() => handleDeactivateTeam(team.id)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-blue-50 rounded p-3">
                        <p className="text-sm font-medium text-blue-600">Total Members</p>
                        <p className="text-2xl font-bold text-blue-900">{team.stats.totalMembers}</p>
                      </div>
                      <div className="bg-green-50 rounded p-3">
                        <p className="text-sm font-medium text-green-600">Coaches</p>
                        <p className="text-2xl font-bold text-green-900">{team.stats.coaches}</p>
                      </div>
                      <div className="bg-purple-50 rounded p-3">
                        <p className="text-sm font-medium text-purple-600">Players</p>
                        <p className="text-2xl font-bold text-purple-900">{team.stats.players}</p>
                      </div>
                      <div className="bg-yellow-50 rounded p-3">
                        <p className="text-sm font-medium text-yellow-600">Parents</p>
                        <p className="text-2xl font-bold text-yellow-900">{team.stats.parents}</p>
                      </div>
                    </div>

                    {/* Team Members by Role */}
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">Members by Role</h5>
                      <div className="space-y-2">
                        {Object.entries(team.membersByRole || {}).map(([role, members]) => (
                          members.length > 0 && (
                            <div key={role} className="bg-gray-50 p-2 rounded">
                              <div className="text-xs font-medium text-gray-700 mb-1">{role}:</div>
                              <div className="text-xs text-gray-600">
                                {members.map(m => m.name).join(', ')}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                {teams.length === 0 && (
                  <p className="text-gray-600 text-center py-8">No teams found.</p>
                )}
              </div>
            </div>
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Organization Management</h3>
                <button
                  onClick={() => setShowCreateOrgModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Organization
                </button>
              </div>

              <div className="space-y-4">
                {organizations.map((org) => (
                  <div key={org.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{org.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{org.description}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Code: {org.organizationCode} • Created: {formatDate(org.createdAt)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            setSelectedOrgForAdmin(org.id);
                            // Ensure users are loaded before opening modal
                            if (allUsers.length === 0) {
                              await fetchAllUsers();
                            }
                            setShowAssignAdminModal(true);
                          }}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                        >
                          Assign Admin
                        </button>
                        <button
                          onClick={() => handleEditOrg(org)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteOrg(org.id)}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-blue-50 rounded p-3">
                        <p className="text-sm font-medium text-blue-600">Total Users</p>
                        <p className="text-2xl font-bold text-blue-900">{org.stats.totalUsers}</p>
                      </div>
                      <div className="bg-green-50 rounded p-3">
                        <p className="text-sm font-medium text-green-600">Total Teams</p>
                        <p className="text-2xl font-bold text-green-900">{org.stats.totalTeams}</p>
                      </div>
                      <div className="bg-purple-50 rounded p-3">
                        <p className="text-sm font-medium text-purple-600">Admins</p>
                        <p className="text-2xl font-bold text-purple-900">{org.stats.totalAdmins}</p>
                      </div>
                    </div>

                    {/* Organization Admins */}
                    <div className="mb-4">
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">Organization Admins</h5>
                      <div className="space-y-2">
                        {org.usersByRole?.owner?.map((admin) => (
                          <div key={admin.id} className="flex items-center justify-between bg-yellow-50 p-2 rounded">
                            <div>
                              <span className="font-medium">{admin.name}</span>
                              <span className="text-xs text-gray-500 ml-2">({admin.email})</span>
                            </div>
                            <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">Owner</span>
                          </div>
                        ))}
                        {org.usersByRole?.admin?.map((admin) => (
                          <div key={admin.id} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                            <div>
                              <span className="font-medium">{admin.name}</span>
                              <span className="text-xs text-gray-500 ml-2">({admin.email})</span>
                            </div>
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">Admin</span>
                          </div>
                        ))}
                        {(!org.usersByRole?.owner?.length && !org.usersByRole?.admin?.length) && (
                          <p className="text-sm text-gray-500">No admins assigned</p>
                        )}
                      </div>
                    </div>

                    {/* Teams */}
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">Teams</h5>
                      <div className="space-y-3">
                        {org.teams?.map((team) => (
                          <div key={team.id} className="border-l-4 border-blue-500 bg-gray-50 p-3 rounded">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{team.name}</p>
                                <p className="text-xs text-gray-600">{team.sport} • {team.ageGroup} • {team.season}</p>
                              </div>
                            </div>
                            <div className="mt-2 space-y-1">
                              {Object.entries(team.membersByRole || {}).map(([role, members]) => (
                                members.length > 0 && (
                                  <div key={role} className="text-xs">
                                    <span className="font-medium text-gray-700">{role}:</span>
                                    <span className="text-gray-600 ml-1">
                                      {members.map(m => m.name).join(', ')}
                                    </span>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        ))}
                        {(!org.teams || org.teams.length === 0) && (
                          <p className="text-sm text-gray-500">No teams in this organization</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {organizations.length === 0 && (
                  <p className="text-gray-600 text-center py-8">No organizations found. Create one to get started.</p>
                )}
              </div>
            </div>
          )}

          {/* Age Groups Tab */}
          {activeTab === 'age-groups' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Age Group Management</h3>
                <button
                  onClick={() => setShowCreateAgeGroupModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Age Group
                </button>
              </div>

              <div className="space-y-4">
                {ageGroups.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No age groups found. Create your first age group to get started.
                  </div>
                ) : (
                  ageGroups.map((ageGroup) => (
                    <div key={ageGroup.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">{ageGroup.name}</h4>
                            {ageGroup.is_active === false && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Inactive
                              </span>
                            )}
                            {ageGroup.is_active !== false && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                            {ageGroup.Organization && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {ageGroup.Organization.name}
                              </span>
                            )}
                            {!ageGroup.Organization && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Global
                              </span>
                            )}
                          </div>
                          {ageGroup.description && (
                            <p className="text-sm text-gray-600 mb-2">{ageGroup.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            {ageGroup.min_birthday && ageGroup.max_birthday && (
                              <span>Birthday Range: {new Date(ageGroup.min_birthday).toLocaleDateString()} - {new Date(ageGroup.max_birthday).toLocaleDateString()}</span>
                            )}
                            {ageGroup.min_birthday && !ageGroup.max_birthday && (
                              <span>Min Birthday: {new Date(ageGroup.min_birthday).toLocaleDateString()}</span>
                            )}
                            {!ageGroup.min_birthday && ageGroup.max_birthday && (
                              <span>Max Birthday: {new Date(ageGroup.max_birthday).toLocaleDateString()}</span>
                            )}
                            {ageGroup.cutoff_date && (
                              <span>Cutoff: {new Date(ageGroup.cutoff_date).toLocaleDateString()}</span>
                            )}
                            {(!ageGroup.min_birthday && !ageGroup.max_birthday) && (ageGroup.min_age !== null || ageGroup.max_age !== null) && (
                              <span>Age Range: {ageGroup.min_age || '?'} - {ageGroup.max_age || '?'}</span>
                            )}
                            <span>Sort Order: {ageGroup.sort_order}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEditAgeGroup(ageGroup);
                            }}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit age group"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteAgeGroup(ageGroup.id);
                            }}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete age group"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Subscription Plans</h3>
                <button
                  onClick={() => setShowCreatePlanModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create New Plan
                </button>
              </div>

              {subscriptionPlans.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                  <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Subscription Plans</h4>
                  <p className="text-gray-600 mb-4">Create your first subscription plan to start accepting payments.</p>
                  <button
                    onClick={() => setShowCreatePlanModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create First Plan
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {subscriptionPlans.map((plan) => (
                    <div key={plan.id} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              plan.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {plan.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="text-sm text-gray-500">Order: {plan.display_order}</span>
                          </div>
                          <p className="text-gray-600 mb-3">{plan.description}</p>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Price: </span>
                              <span className="text-gray-900">${parseFloat(plan.amount).toFixed(2)}/{plan.interval}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Stripe Price ID: </span>
                              <span className="text-gray-600 font-mono text-xs">{plan.stripe_price_id}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Max Teams: </span>
                              <span className="text-gray-900">{plan.max_teams || 'Unlimited'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Max Users/Team: </span>
                              <span className="text-gray-900">{plan.max_users_per_team || 'Unlimited'}</span>
                            </div>
                          </div>
                          {plan.features && plan.features.length > 0 && (
                            <div className="mt-3">
                              <span className="font-medium text-gray-700 text-sm">Features: </span>
                              <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                                {plan.features.map((feature, idx) => (
                                  <li key={idx}>{feature}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleEditPlan(plan)}
                          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          <PencilIcon className="h-4 w-4 inline mr-1" />
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Platform Stripe Tab */}
          {activeTab === 'platform-stripe' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Platform Stripe Account</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This is the main Stripe account used for user subscriptions. All subscription payments go to this account.
                  Coaches and organizations use Stripe Connect (separate accounts) to accept payments.
                </p>
              </div>

              <OrganizationStripeModes />

              {platformStripeLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : platformStripeAccount?.connected ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Connected</h4>
                        <p className="text-sm text-gray-600">Platform Stripe account is configured</p>
                      </div>
                    </div>
                    <button
                      onClick={fetchPlatformStripeAccount}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                    >
                      Refresh
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account ID</label>
                      <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">{platformStripeAccount.account.id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{platformStripeAccount.account.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{platformStripeAccount.account.country?.toUpperCase() || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{platformStripeAccount.account.default_currency?.toUpperCase() || 'USD'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Charges Enabled</label>
                      <p className={`text-sm font-medium bg-gray-50 p-2 rounded ${
                        platformStripeAccount.account.charges_enabled ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {platformStripeAccount.account.charges_enabled ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payouts Enabled</label>
                      <p className={`text-sm font-medium bg-gray-50 p-2 rounded ${
                        platformStripeAccount.account.payouts_enabled ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {platformStripeAccount.account.payouts_enabled ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>

                  {(!platformStripeAccount.account.charges_enabled || !platformStripeAccount.account.payouts_enabled) && (
                    <div className={`mt-6 p-4 border rounded-lg ${
                      platformStripeAccount.isTestMode 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-start">
                        <svg className={`h-5 w-5 mt-0.5 mr-2 ${
                          platformStripeAccount.isTestMode ? 'text-blue-600' : 'text-yellow-600'
                        }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          {platformStripeAccount.isTestMode ? (
                            <>
                              <h5 className="font-medium text-blue-900 mb-2">Test Mode - Charges/Payouts Status</h5>
                              <p className="text-sm text-blue-800 mb-2">
                                You're currently in <strong>test mode</strong>. In test mode, you can still process test payments even if charges and payouts show as disabled.
                              </p>
                              <p className="text-sm text-blue-800 mb-3">
                                To enable charges and payouts in test mode, you can activate your test account in the Stripe Dashboard, but it's not required for testing. Test payments will work regardless.
                              </p>
                              <div className="flex gap-2">
                                <a
                                  href="https://dashboard.stripe.com/test/settings/account"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                                >
                                  Open Test Dashboard
                                  <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                                <a
                                  href="https://stripe.com/docs/testing"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium"
                                >
                                  Test Card Numbers
                                </a>
                              </div>
                            </>
                          ) : (
                            <>
                              <h5 className="font-medium text-yellow-900 mb-2">Account Activation Required</h5>
                              <p className="text-sm text-yellow-800 mb-3">
                                Your Stripe account needs to be activated to accept payments. Complete the activation process in your Stripe Dashboard.
                              </p>
                              <a
                                href="https://dashboard.stripe.com/settings/account"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                              >
                                Complete Activation in Stripe Dashboard
                                <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Test and live keys are both stored as server environment variables (<code className="bg-blue-100 px-1 rounded">STRIPE_TEST_*</code> and <code className="bg-blue-100 px-1 rounded">STRIPE_LIVE_*</code>).
                      <code className="bg-blue-100 px-1 rounded">STRIPE_ENVIRONMENT</code> (<code className="bg-blue-100 px-1 rounded">test</code> or <code className="bg-blue-100 px-1 rounded">live</code>) chooses which set is used; changing it restarts the application.
                      Organizations must connect Stripe separately in each mode.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-red-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Not Connected</h4>
                      <p className="text-sm text-gray-600">Platform Stripe account is not configured</p>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800">
                      <strong>Error:</strong> {platformStripeAccount?.error || 'Unable to connect to Stripe'}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h5 className="font-medium text-gray-900">Setup Instructions:</h5>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      <li>Get your Stripe API keys from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Dashboard</a></li>
                      <li>Set <code className="bg-gray-100 px-1 rounded">STRIPE_TEST_SECRET_KEY</code> / <code className="bg-gray-100 px-1 rounded">STRIPE_TEST_PUBLISHABLE_KEY</code> (and the <code className="bg-gray-100 px-1 rounded">STRIPE_LIVE_*</code> equivalents)</li>
                      <li>Set <code className="bg-gray-100 px-1 rounded">STRIPE_ENVIRONMENT</code> to <code className="bg-gray-100 px-1 rounded">test</code> or <code className="bg-gray-100 px-1 rounded">live</code></li>
                      <li>Restart your server</li>
                      <li>Click "Refresh" to verify the connection</li>
                    </ol>
                  </div>

                  <button
                    onClick={fetchPlatformStripeAccount}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Refresh Connection
                  </button>
                </div>
              )}
            </div>
          )}

          {/* System Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">System Administration</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Database Operations</h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleSystemAction('backup')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                    >
                      Create Database Backup
                    </button>
                    <button
                      onClick={() => handleSystemAction('optimize')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                    >
                      Optimize Database
                    </button>
                    <button
                      onClick={() => handleSystemAction('cleanup')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                    >
                      Cleanup Old Data
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">System Maintenance</h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleSystemAction('restart')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                    >
                      Restart Services
                    </button>
                    <button
                      onClick={() => handleSystemAction('update')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                    >
                      Check for Updates
                    </button>
                    <button
                      onClick={() => handleSystemAction('logs')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                    >
                      View System Logs
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New User</h3>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteData({
                      email: '',
                      name: '',
                      password: '',
                      role: 'player',
                      teamId: '',
                      organizationId: '',
                      organizationRole: 'viewer',
                      is_active: true
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleInviteUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      value={inviteData.name}
                      onChange={(e) => setInviteData({...inviteData, name: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email *</label>
                    <input
                      type="email"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <input
                      type="password"
                      value={inviteData.password}
                      onChange={(e) => setInviteData({...inviteData, password: e.target.value})}
                      placeholder="Leave blank for random password"
                      minLength={6}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Minimum 6 characters. If blank, a random password will be generated.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role *</label>
                    <select
                      value={inviteData.role}
                      onChange={(e) => setInviteData({...inviteData, role: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="player">Player</option>
                      <option value="coach">Coach</option>
                      <option value="assistant_coach">Assistant Coach</option>
                      <option value="parent">Parent</option>
                      <option value="org_admin">Organization Admin</option>
                      <option value="admin">Admin</option>
                      <option value="app_manager">App Manager</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Team</label>
                  <select
                    value={inviteData.teamId}
                    onChange={(e) => setInviteData({...inviteData, teamId: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No Team (Optional)</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.sport} - {team.ageGroup})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization</label>
                    <select
                      value={inviteData.organizationId}
                      onChange={(e) => setInviteData({...inviteData, organizationId: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">No Organization (Optional)</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {inviteData.organizationId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Organization Role</label>
                      <select
                        value={inviteData.organizationRole}
                        onChange={(e) => setInviteData({...inviteData, organizationRole: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inviteData.is_active}
                      onChange={(e) => setInviteData({...inviteData, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">User is active</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteData({
                        email: '',
                        name: '',
                        password: '',
                        role: 'player',
                        teamId: '',
                        organizationId: '',
                        organizationRole: 'viewer',
                        is_active: true
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateOrgModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Organization</h3>
              <form onSubmit={handleCreateOrganization} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                  <input
                    type="text"
                    value={newOrgData.name}
                    onChange={(e) => setNewOrgData({...newOrgData, name: e.target.value})}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newOrgData.description}
                    onChange={(e) => setNewOrgData({...newOrgData, description: e.target.value})}
                    required
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateOrgModal(false);
                      setNewOrgData({ name: '', description: '' });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create Organization
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Assign Admin Modal */}
      {showAssignAdminModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Assign Organization Admin</h3>
              <form onSubmit={handleAssignAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">User</label>
                  <select
                    value={assignAdminData.userId}
                    onChange={(e) => setAssignAdminData({...assignAdminData, userId: e.target.value})}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a user</option>
                    {allUsers.length === 0 ? (
                      <option value="" disabled>Loading users...</option>
                    ) : (
                      allUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email}) - {user.role}
                        </option>
                      ))
                    )}
                  </select>
                  {allUsers.length === 0 && (
                    <p className="mt-1 text-sm text-gray-500">No users found. Please check your permissions.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={assignAdminData.role}
                    onChange={(e) => setAssignAdminData({...assignAdminData, role: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignAdminModal(false);
                      setSelectedOrgForAdmin(null);
                      setAssignAdminData({ userId: '', role: 'admin' });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Assign Admin
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit User</h3>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={editUserData.name}
                    onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={editUserData.email}
                    onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={editUserData.role}
                    onChange={(e) => setEditUserData({...editUserData, role: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="player">Player</option>
                    <option value="parent">Parent</option>
                    <option value="coach">Coach</option>
                    <option value="assistant_coach">Assistant Coach</option>
                    <option value="admin">Admin</option>
                    <option value="org_admin">Organization Admin</option>
                    <option value="app_manager">App Manager</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editUserData.is_active}
                    onChange={(e) => setEditUserData({...editUserData, is_active: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Active</label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditUserModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditTeamModal && selectedTeam && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Team</h3>
              <form onSubmit={handleUpdateTeam} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Team Name</label>
                  <input
                    type="text"
                    value={editTeamData.name}
                    onChange={(e) => setEditTeamData({...editTeamData, name: e.target.value})}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sport</label>
                    <input
                      type="text"
                      value={editTeamData.sport}
                      onChange={(e) => setEditTeamData({...editTeamData, sport: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Age Group</label>
                    <select
                      value={editTeamData.age_group_id}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setEditTeamData({...editTeamData, age_group_id: '', ageGroup: ''});
                        } else {
                          const selected = filteredAgeGroupsForTeam.find(ag => ag.id === parseInt(value, 10));
                          setEditTeamData({...editTeamData, age_group_id: value, ageGroup: selected?.name || ''});
                        }
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">No Age Group</option>
                      {filteredAgeGroupsForTeam.map(ag => (
                        <option key={ag.id} value={ag.id}>
                          {ag.name}{ag.organization_id ? ' (Org)' : ' (Global)'}
                        </option>
                      ))}
                    </select>
                    {editTeamData.ageGroup && (
                      <p className="mt-1 text-xs text-gray-500">Current label: {editTeamData.ageGroup}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Season</label>
                  <input
                    type="text"
                    value={editTeamData.season}
                    onChange={(e) => setEditTeamData({...editTeamData, season: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Organization</label>
                  <select
                    value={editTeamData.organization_id}
                    onChange={(e) => setEditTeamData({...editTeamData, organization_id: e.target.value, age_group_id: '', ageGroup: ''})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No Organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editTeamData.isActive}
                    onChange={(e) => setEditTeamData({...editTeamData, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Active</label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditTeamModal(false);
                      setSelectedTeam(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update Team
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditOrgModal && selectedOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Organization</h3>
              <form onSubmit={handleUpdateOrg} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                  <input
                    type="text"
                    value={editOrgData.name}
                    onChange={(e) => setEditOrgData({...editOrgData, name: e.target.value})}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={editOrgData.description}
                    onChange={(e) => setEditOrgData({...editOrgData, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditOrgModal(false);
                      setSelectedOrg(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update Organization
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Age Group Modal */}
      {showCreateAgeGroupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Age Group</h3>
              <form onSubmit={handleCreateAgeGroup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={newAgeGroupData.name}
                    onChange={(e) => setNewAgeGroupData({...newAgeGroupData, name: e.target.value})}
                    required
                    placeholder="e.g., 12U, 14U, Varsity"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newAgeGroupData.description}
                    onChange={(e) => setNewAgeGroupData({...newAgeGroupData, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Playing Age Range (recommended)</label>
                  <p className="text-xs text-gray-500 mb-3">Players qualify based on their baseball playing age (age as of May 1 of the season, rolling forward automatically each year) — no manual updates needed each season.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Min Age</label>
                      <input
                        type="number"
                        min="0"
                        value={newAgeGroupData.min_age}
                        onChange={(e) => setNewAgeGroupData({...newAgeGroupData, min_age: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Max Age</label>
                      <input
                        type="number"
                        min="0"
                        value={newAgeGroupData.max_age}
                        onChange={(e) => setNewAgeGroupData({...newAgeGroupData, max_age: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">e.g. for "13U", set both Min and Max to 13</p>
                </div>
                <details>
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">Birthday Range (legacy, optional)</summary>
                  <p className="text-xs text-gray-500 mt-2 mb-3">Pins eligibility to a fixed birth-date window for one season only — it will not roll forward next year. Leave blank unless you specifically need a fixed cohort. If set, this takes priority over the age range above.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Earliest Birthday (Min)</label>
                      <input
                        type="date"
                        value={newAgeGroupData.min_birthday}
                        onChange={(e) => setNewAgeGroupData({...newAgeGroupData, min_birthday: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">Youngest players (latest birthday)</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Latest Birthday (Max)</label>
                      <input
                        type="date"
                        value={newAgeGroupData.max_birthday}
                        onChange={(e) => setNewAgeGroupData({...newAgeGroupData, max_birthday: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">Oldest players (earliest birthday)</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cutoff Date (Reference)</label>
                    <input
                      type="date"
                      value={newAgeGroupData.cutoff_date}
                      onChange={(e) => setNewAgeGroupData({...newAgeGroupData, cutoff_date: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Reference date for age calculation (e.g., season start date)</p>
                  </div>
                </details>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Organization</label>
                  <select
                    value={newAgeGroupData.organization_id}
                    onChange={(e) => setNewAgeGroupData({...newAgeGroupData, organization_id: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Global (Available to all)</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sort Order</label>
                    <input
                      type="number"
                      value={newAgeGroupData.sort_order}
                      onChange={(e) => setNewAgeGroupData({...newAgeGroupData, sort_order: e.target.value})}
                      min="0"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      checked={newAgeGroupData.is_active}
                      onChange={(e) => setNewAgeGroupData({...newAgeGroupData, is_active: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">Active</label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateAgeGroupModal(false);
                      setNewAgeGroupData({ name: '', description: '', min_birthday: '', max_birthday: '', cutoff_date: '', organization_id: '', is_active: true, sort_order: 0 });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create Age Group
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Age Group Modal */}
      {showEditAgeGroupModal && selectedAgeGroup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Age Group</h3>
              <form onSubmit={handleUpdateAgeGroup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={editAgeGroupData.name}
                    onChange={(e) => setEditAgeGroupData({...editAgeGroupData, name: e.target.value})}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={editAgeGroupData.description}
                    onChange={(e) => setEditAgeGroupData({...editAgeGroupData, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Playing Age Range (recommended)</label>
                  <p className="text-xs text-gray-500 mb-3">Players qualify based on their baseball playing age (age as of May 1 of the season, rolling forward automatically each year) — no manual updates needed each season.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Min Age</label>
                      <input
                        type="number"
                        min="0"
                        value={editAgeGroupData.min_age}
                        onChange={(e) => setEditAgeGroupData({...editAgeGroupData, min_age: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Max Age</label>
                      <input
                        type="number"
                        min="0"
                        value={editAgeGroupData.max_age}
                        onChange={(e) => setEditAgeGroupData({...editAgeGroupData, max_age: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">e.g. for "13U", set both Min and Max to 13</p>
                </div>
                <details>
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">Birthday Range (legacy, optional)</summary>
                  <p className="text-xs text-gray-500 mt-2 mb-3">Pins eligibility to a fixed birth-date window for one season only — it will not roll forward next year. Leave blank unless you specifically need a fixed cohort. If set, this takes priority over the age range above.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Earliest Birthday (Min)</label>
                      <input
                        type="date"
                        value={editAgeGroupData.min_birthday}
                        onChange={(e) => setEditAgeGroupData({...editAgeGroupData, min_birthday: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">Youngest players (latest birthday)</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Latest Birthday (Max)</label>
                      <input
                        type="date"
                        value={editAgeGroupData.max_birthday}
                        onChange={(e) => setEditAgeGroupData({...editAgeGroupData, max_birthday: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">Oldest players (earliest birthday)</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cutoff Date (Reference)</label>
                    <input
                      type="date"
                      value={editAgeGroupData.cutoff_date}
                      onChange={(e) => setEditAgeGroupData({...editAgeGroupData, cutoff_date: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Reference date for age calculation (e.g., season start date)</p>
                  </div>
                </details>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Organization</label>
                  <select
                    value={editAgeGroupData.organization_id}
                    onChange={(e) => setEditAgeGroupData({...editAgeGroupData, organization_id: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Global (Available to all)</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sort Order</label>
                    <input
                      type="number"
                      value={editAgeGroupData.sort_order}
                      onChange={(e) => setEditAgeGroupData({...editAgeGroupData, sort_order: e.target.value})}
                      min="0"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      checked={editAgeGroupData.is_active}
                      onChange={(e) => setEditAgeGroupData({...editAgeGroupData, is_active: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">Active</label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditAgeGroupModal(false);
                      setSelectedAgeGroup(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update Age Group
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Subscription Plan Modal */}
      {showCreatePlanModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Subscription Plan</h3>
                <button
                  onClick={() => {
                    setShowCreatePlanModal(false);
                    setNewPlanData({ name: '', description: '', amount: '', stripe_price_id: '', stripe_product_id: '', interval: 'month', interval_count: 1, is_active: true, display_order: 0, features: [], max_teams: '', max_users_per_team: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreatePlan} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Plan Name *</label>
                    <input
                      type="text"
                      value={newPlanData.name}
                      onChange={(e) => setNewPlanData({...newPlanData, name: e.target.value})}
                      required
                      placeholder="e.g., Basic, Pro, Enterprise"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Amount ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPlanData.amount}
                      onChange={(e) => setNewPlanData({...newPlanData, amount: e.target.value})}
                      required
                      placeholder="9.99"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newPlanData.description}
                    onChange={(e) => setNewPlanData({...newPlanData, description: e.target.value})}
                    rows={2}
                    placeholder="Describe what this plan includes..."
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stripe Product ID *</label>
                    <input
                      type="text"
                      value={newPlanData.stripe_product_id}
                      onChange={(e) => setNewPlanData({...newPlanData, stripe_product_id: e.target.value})}
                      required
                      placeholder="prod_xxxxx"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Create a product in Stripe Dashboard first</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stripe Price ID *</label>
                    <input
                      type="text"
                      value={newPlanData.stripe_price_id}
                      onChange={(e) => setNewPlanData({...newPlanData, stripe_price_id: e.target.value})}
                      required
                      placeholder="price_xxxxx"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Create a price for the product in Stripe</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Billing Interval *</label>
                    <select
                      value={newPlanData.interval}
                      onChange={(e) => setNewPlanData({...newPlanData, interval: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Display Order</label>
                    <input
                      type="number"
                      min="0"
                      value={newPlanData.display_order}
                      onChange={(e) => setNewPlanData({...newPlanData, display_order: parseInt(e.target.value) || 0})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Teams</label>
                    <input
                      type="number"
                      min="0"
                      value={newPlanData.max_teams}
                      onChange={(e) => setNewPlanData({...newPlanData, max_teams: e.target.value})}
                      placeholder="Leave empty for unlimited"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Users per Team</label>
                    <input
                      type="number"
                      min="0"
                      value={newPlanData.max_users_per_team}
                      onChange={(e) => setNewPlanData({...newPlanData, max_users_per_team: e.target.value})}
                      placeholder="Leave empty for unlimited"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newPlanData.is_active}
                    onChange={(e) => setNewPlanData({...newPlanData, is_active: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">Active (visible to users)</label>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Before creating:</strong> Make sure you've created a Product and Price in your Stripe Dashboard. 
                    The Price ID should match the amount and interval you specify here.
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreatePlanModal(false);
                      setNewPlanData({ name: '', description: '', amount: '', stripe_price_id: '', stripe_product_id: '', interval: 'month', interval_count: 1, is_active: true, display_order: 0, features: [], max_teams: '', max_users_per_team: '' });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create Plan
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subscription Plan Modal */}
      {showEditPlanModal && selectedPlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Subscription Plan</h3>
                <button
                  onClick={() => {
                    setShowEditPlanModal(false);
                    setSelectedPlan(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleUpdatePlan} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Plan Name *</label>
                    <input
                      type="text"
                      value={editPlanData.name}
                      onChange={(e) => setEditPlanData({...editPlanData, name: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Amount ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editPlanData.amount}
                      onChange={(e) => setEditPlanData({...editPlanData, amount: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Note: If you change the amount, create a new Stripe price and update the Price ID below</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={editPlanData.description}
                    onChange={(e) => setEditPlanData({...editPlanData, description: e.target.value})}
                    rows={2}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stripe Product ID *</label>
                    <input
                      type="text"
                      value={editPlanData.stripe_product_id}
                      onChange={(e) => setEditPlanData({...editPlanData, stripe_product_id: e.target.value})}
                      required
                      placeholder="prod_xxxxx"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stripe Price ID *</label>
                    <input
                      type="text"
                      value={editPlanData.stripe_price_id}
                      onChange={(e) => setEditPlanData({...editPlanData, stripe_price_id: e.target.value})}
                      required
                      placeholder="price_xxxxx"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Create a new price in Stripe if you changed the amount.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Teams</label>
                    <input
                      type="number"
                      min="0"
                      value={editPlanData.max_teams}
                      onChange={(e) => setEditPlanData({...editPlanData, max_teams: e.target.value})}
                      placeholder="Leave empty for unlimited"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Users per Team</label>
                    <input
                      type="number"
                      min="0"
                      value={editPlanData.max_users_per_team}
                      onChange={(e) => setEditPlanData({...editPlanData, max_users_per_team: e.target.value})}
                      placeholder="Leave empty for unlimited"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={editPlanData.display_order}
                    onChange={(e) => setEditPlanData({...editPlanData, display_order: parseInt(e.target.value) || 0})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editPlanData.is_active}
                    onChange={(e) => setEditPlanData({...editPlanData, is_active: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">Active (visible to users)</label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditPlanModal(false);
                      setSelectedPlan(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update Plan
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default AppManagement;